import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccountTreeOutlined,
  ApiOutlined,
  AutoAwesomeOutlined,
  CheckCircleOutlined,
  ExtensionOutlined,
  MenuBookOutlined,
} from "@mui/icons-material";
import useToast from "../../hooks/useToast";
import { TOAST } from "../../constants/ToastMessages";

import BottomActionBar from "../../components/BottomActionBar";
import CodeEditor from "../../components/CodeEditor";
import JsonEditor from "../../components/JsonEditor";
import LoadingSpinner from "../../components/LoadingSpinner";
import TrimTextField from "../../components/TrimTextField";
import useApiCall from "../../hooks/useApiCall";
import { INDIGO, SLATE } from "../../constants/Colors";
import { usePageMeta } from "../../contexts/PageMetaContext";

// ─── Icons ────────────────────────────────────────────────────────────────────

const CAPABILITY_ICONS = {
  http_call:    { Icon: ApiOutlined,         bg: "#eff6ff", color: "#1d4ed8" },
  rag_api_tool: { Icon: MenuBookOutlined,    bg: "#f0fdf4", color: "#15803d" },
  mcp_client:   { Icon: AccountTreeOutlined, bg: "#f5f3ff", color: "#6d28d9" },
  llm_tool:     { Icon: AutoAwesomeOutlined, bg: "#fff7ed", color: "#c2410c" },
};

function CapabilityIcon({ type, size = 48 }) {
  const cfg = CAPABILITY_ICONS[type] || { Icon: ExtensionOutlined, bg: SLATE[100], color: SLATE[500] };
  return (
    <Box sx={{ width: size, height: size, borderRadius: "12px", bgcolor: cfg.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <cfg.Icon sx={{ fontSize: size * 0.5, color: cfg.color }} />
    </Box>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const FIELD_LABEL_SX = { fontSize: "0.8125rem", fontWeight: 600, color: SLATE[800], mb: "6px", display: "block" };
const FIELD_HINT_SX  = { fontSize: "0.75rem",   color: SLATE[400], mt: "4px", display: "block" };
const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", fontSize: "0.875rem", bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[200] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

// ─── Dynamic config rendering ─────────────────────────────────────────────────

/**
 * Metadata for known fields that need special rendering.
 * Add entries here when a new field needs non-default treatment.
 * Fields not listed fall back to auto-detection based on value type.
 */
const FIELD_META = {
  method:        { type: "select",  options: ["GET", "POST", "PUT", "PATCH", "DELETE"], label: "Method",              hint: "HTTP method" },
  headers:       { type: "json",    label: "Headers (JSON)",       hint: "Request headers as JSON object" },
  body:          { type: "json",    label: "Request Body (JSON)",  hint: "Request body template. Use {{variable}} for dynamic values." },
  payload:       { type: "json",    label: "Payload (JSON)",       hint: "Default payload structure for this capability" },
  output_format: { type: "json",    label: "Output Format (JSON)", hint: "Expected output format or schema" },
  output_schema: { type: "json",    label: "Output Schema (JSON)", hint: "Expected output schema for the response" },
};

// Keys that are internal and should not be shown as editable config fields
const EXCLUDED_KEYS = new Set(["tools_input"]);

/**
 * Defines ALL fields that should always be visible for each capability type,
 * with empty defaults. Merged with capability.configuration so fields that
 * were left blank during creation still appear.
 * Add one entry here when a new capability type is introduced.
 */
const CAPABILITY_TYPE_DEFAULTS = {
  http_call:    { url: "", method: "GET", headers: "", body: "" },
  rag_api_tool: { url: "", method: "GET", headers: "", body: "" },
  mcp_client:   { mcp_server_url: "", mcp_tool_name: "" },
  llm_tool:     { payload: "", output_format: "" },
};

function toLabel(key) {
  return key.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function detectFieldType(value) {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number")  return "number";
  if (value !== null && typeof value === "object") return "json";
  return "string";
}

function toDisplayValue(value) {
  if (value !== null && typeof value === "object") return JSON.stringify(value, null, 2);
  return value ?? "";
}

function fromDisplayValue(displayVal, fieldType) {
  if (fieldType === "json") {
    try { return displayVal ? JSON.parse(displayVal) : undefined; } catch { return displayVal || undefined; }
  }
  if (fieldType === "number") return displayVal === "" ? undefined : Number(displayVal);
  if (fieldType === "boolean") return displayVal === "true" || displayVal === true;
  return displayVal || undefined;
}

/**
 * Renders config fields dynamically based on capability.configuration keys.
 * No per-type hardcoding — just add a FIELD_META entry for special fields.
 */
function DynamicConfigFields({ capConfig = {}, values = {}, onChange, jsonErrors = {} }) {
  const keys = Object.keys(capConfig).filter((k) => !EXCLUDED_KEYS.has(k));
  if (keys.length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {keys.map((key) => {
        const meta      = FIELD_META[key];
        const fieldType = meta?.type || detectFieldType(capConfig[key]);
        const label     = meta?.label || toLabel(key);
        const hint      = meta?.hint  || "";
        const value     = values[key] ?? toDisplayValue(capConfig[key]);

        return (
          <Box key={key}>
            <Typography component="label" sx={FIELD_LABEL_SX}>{label}</Typography>
            {hint && <Typography sx={{ ...FIELD_HINT_SX, mt: 0, mb: "8px" }}>{hint}</Typography>}

            {fieldType === "select" && (
              <TextField
                select fullWidth size="small"
                value={value || meta.options[0]}
                onChange={(e) => onChange(key, e.target.value)}
                sx={INPUT_SX}
              >
                {meta.options.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </TextField>
            )}

            {fieldType === "json" && (
              <JsonEditor
                value={typeof value === "object" ? JSON.stringify(value, null, 2) : (value || "")}
                onChange={(val) => onChange(key, val)}
                error={!!jsonErrors[key]}
                helperText={jsonErrors[key] ? `Invalid JSON: ${jsonErrors[key]}` : ""}
                height="140px"
              />
            )}

            {fieldType === "boolean" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch
                  checked={value === true || value === "true"}
                  onChange={(e) => onChange(key, e.target.checked)}
                  size="small"
                  sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: INDIGO[600] }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: INDIGO[400] } }}
                />
                <Typography sx={{ fontSize: "0.875rem", color: SLATE[700] }}>
                  {value === true || value === "true" ? "Enabled" : "Disabled"}
                </Typography>
              </Box>
            )}

            {fieldType === "number" && (
              <TextField
                fullWidth size="small" type="number"
                value={value}
                onChange={(e) => onChange(key, e.target.value)}
                sx={INPUT_SX}
              />
            )}

            {fieldType === "string" && (
              <TrimTextField
                fullWidth size="small"
                value={value}
                onChange={(e) => onChange(key, e.target.value)}
                sx={INPUT_SX}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Full config = type defaults merged with stored configuration (stored values win)
function getMergedConfig(capType, capConfig = {}) {
  const typeDefaults = CAPABILITY_TYPE_DEFAULTS[capType] || {};
  return { ...typeDefaults, ...capConfig };
}

// Merge saved overrides with full config defaults
function initOverrides(capType, capConfig = {}, savedOverrides = {}) {
  const merged = getMergedConfig(capType, capConfig);
  const out = {};
  Object.keys(merged)
    .filter((k) => !EXCLUDED_KEYS.has(k))
    .forEach((k) => {
      const saved = savedOverrides[k];
      out[k] = saved !== undefined ? toDisplayValue(saved) : toDisplayValue(merged[k]);
    });
  return out;
}

// Serialize display values back to proper types before saving
function buildSaveOverrides(capType, capConfig = {}, displayValues = {}) {
  const merged = getMergedConfig(capType, capConfig);
  const out = {};
  Object.keys(merged)
    .filter((k) => !EXCLUDED_KEYS.has(k))
    .forEach((k) => {
      const meta      = FIELD_META[k];
      const fieldType = meta?.type || detectFieldType(merged[k]);
      const val       = fromDisplayValue(displayValues[k], fieldType);
      if (val !== undefined) out[k] = val;
    });
  return out;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ManageCapability() {
  const { id, app_name, cap_id } = useParams();
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();
  const tokens          = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { success, error: toastError } = useToast();
  const { setPageMeta } = usePageMeta();

  const isNew       = cap_id === "new";
  const capabilityId = searchParams.get("capability_id");

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);

  const [capability, setCapability] = useState(null);
  const [agentCap, setAgentCap]     = useState(null);

  const [customInstructions, setCustomInstructions]     = useState("");
  const [originalInstructions, setOriginalInstructions] = useState("");

  const [configValues, setConfigValues] = useState({});
  const originalConfigRef               = useRef({});
  const [jsonErrors, setJsonErrors]     = useState({});

  // Schema overrides (http_call / rag_api_tool only)
  const [inputSchema, setInputSchema]       = useState("");
  const [outputSchema, setOutputSchema]     = useState("");
  const [schemaErrors, setSchemaErrors]     = useState({ input: null, output: null });
  const originalInputSchemaRef              = useRef("");
  const originalOutputSchemaRef             = useRef("");

  // Python code overrides — always shown since they're top-level fields on every capability
  const [beforeCode, setBeforeCode]         = useState("");
  const [afterCode, setAfterCode]           = useState("");
  const originalBeforeCodeRef               = useRef("");
  const originalAfterCodeRef                = useRef("");

  useEffect(() => { fetchData(); }, [cap_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { return () => setPageMeta({}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      if (isNew) {
        const res = await makeRequest(`/v2/capabilities/${capabilityId}`, { method: "GET", authTokens: tokens });
        const cap = res?.capability;
        setCapability(cap);
        const init = initOverrides(cap?.capability_type, cap?.configuration);
        setConfigValues(init);
        originalConfigRef.current = init;
        const bc = cap?.before_capability_code || "";
        const ac2 = cap?.after_capability_code || "";
        setBeforeCode(bc); originalBeforeCodeRef.current = bc;
        setAfterCode(ac2); originalAfterCodeRef.current  = ac2;
        const is = JSON.stringify(cap?.input_schema ?? [], null, 2);
        const os = JSON.stringify(cap?.output_schema ?? [], null, 2);
        setInputSchema(is);  originalInputSchemaRef.current  = is;
        setOutputSchema(os); originalOutputSchemaRef.current = os;
        setPageMeta({ itemName: cap?.display_name || cap?.name });
      } else {
        const res = await makeRequest(`/v2/agents/${id}/capabilities/${cap_id}`, { method: "GET", authTokens: tokens });
        const ac  = res?.agent_capability;
        setAgentCap(ac);
        setCapability(ac?.capability || {});

        const instructions = ac?.configuration_overrides?.custom_instructions || "";
        setCustomInstructions(instructions);
        setOriginalInstructions(instructions);

        const init = initOverrides(ac?.capability?.capability_type, ac?.capability?.configuration, ac?.configuration_overrides);
        setConfigValues(init);
        originalConfigRef.current = init;
        const bc = ac?.configuration_overrides?.before_capability_code ?? ac?.capability?.before_capability_code ?? "";
        const ac2 = ac?.configuration_overrides?.after_capability_code  ?? ac?.capability?.after_capability_code  ?? "";
        setBeforeCode(bc); originalBeforeCodeRef.current = bc;
        setAfterCode(ac2); originalAfterCodeRef.current  = ac2;
        const is = JSON.stringify(ac?.configuration_overrides?.input_schema  ?? ac?.capability?.input_schema  ?? [], null, 2);
        const os = JSON.stringify(ac?.configuration_overrides?.output_schema ?? ac?.capability?.output_schema ?? [], null, 2);
        setInputSchema(is);  originalInputSchemaRef.current  = is;
        setOutputSchema(os); originalOutputSchemaRef.current = os;

        setPageMeta({ itemName: ac?.capability?.display_name || ac?.capability_instance_name || ac?.capability?.name });
      }
    } catch {
      // error already toasted
    } finally {
      setLoading(false);
    }
  };

  const capType       = capability?.capability_type;
  const mergedCapConfig = getMergedConfig(capType, capability?.configuration);

  const handleConfigChange = (key, value) => {
    const meta      = FIELD_META[key];
    const fieldType = meta?.type || detectFieldType(mergedCapConfig[key]);
    if (fieldType === "json" && value) {
      try { JSON.parse(value); setJsonErrors((p) => ({ ...p, [key]: null })); }
      catch (e) { setJsonErrors((p) => ({ ...p, [key]: e.message })); }
    } else {
      setJsonErrors((p) => ({ ...p, [key]: null }));
    }
    setConfigValues((p) => ({ ...p, [key]: value }));
  };

  const hasJsonErrors = Object.values(jsonErrors).some(Boolean) || schemaErrors.input || schemaErrors.output;

  const isDirty = isNew
    ? true
    : customInstructions !== originalInstructions ||
      JSON.stringify(configValues) !== JSON.stringify(originalConfigRef.current) ||
      beforeCode   !== originalBeforeCodeRef.current  ||
      afterCode    !== originalAfterCodeRef.current   ||
      inputSchema  !== originalInputSchemaRef.current ||
      outputSchema !== originalOutputSchemaRef.current;

  const parseSchema = (str) => { try { return str ? JSON.parse(str) : undefined; } catch { return undefined; } };

  const buildPayload = () => {
    const payload = {
      custom_instructions: customInstructions,
      ...buildSaveOverrides(capability?.capability_type, capability?.configuration, configValues),
      ...(beforeCode ? { before_capability_code: beforeCode } : {}),
      ...(afterCode  ? { after_capability_code:  afterCode  } : {}),
    };
    const is = parseSchema(inputSchema);
    const os = parseSchema(outputSchema);
    if (is !== undefined) payload.input_schema  = is;
    if (os !== undefined) payload.output_schema = os;
    return payload;
  };

  const handleEnable = async () => {
    if (hasJsonErrors) { toastError(TOAST.capability.jsonErrorEnable); return; }
    setSaving(true);
    try {
      const res = await makeRequest(`/v2/agents/${id}/capabilities`, {
        method: "POST",
        body: {
          agent_capability: {
            capability_id: capabilityId,
            capability_instance_name: capability?.name,
            enabled: true,
            priority: 1,
            configuration_overrides: buildPayload(),
          },
        },
        authTokens: tokens,
      });
      success(TOAST.capability.enabled);
      const newCapId = res?.agent_capability?.id;
      navigate(
        newCapId ? `/${app_name}/agent/${id}/capabilities/${newCapId}` : `/${app_name}/agent/${id}/capabilities`,
        { replace: true },
      );
    } catch {
      // error already toasted
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (hasJsonErrors) { toastError(TOAST.capability.jsonError); return; }
    setSaving(true);
    try {
      await makeRequest(`/v2/agents/${id}/capabilities/${cap_id}`, {
        method: "PATCH",
        body: {
          agent_capability: {
            configuration_overrides: { ...agentCap?.configuration_overrides, ...buildPayload() },
          },
        },
        authTokens: tokens,
      });
      success(TOAST.capability.saved);
      setOriginalInstructions(customInstructions);
      originalConfigRef.current = { ...configValues };
    } catch {
      // error already toasted
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    setDisabling(true);
    try {
      await makeRequest(`/v2/agents/${id}/capabilities/${cap_id}`, { method: "DELETE", authTokens: tokens });
      success(TOAST.capability.disabled);
      navigate(`/${app_name}/agent/${id}/capabilities`);
    } catch {
      // error already toasted
    } finally {
      setDisabling(false);
      setConfirmDisable(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading capability..." />;

  const capName = capability?.display_name || agentCap?.capability_instance_name || capability?.name || "Capability";
  const hasConfigFields = Object.keys(mergedCapConfig).some((k) => !EXCLUDED_KEYS.has(k));

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header card */}
      <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${isNew ? SLATE[200] : "#bbf7d0"}`, borderRadius: "16px", p: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <CapabilityIcon type={capability?.capability_type} />
          <Box>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>{capName}</Typography>
            {capability?.description && (
              <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], mt: "2px" }}>{capability.description}</Typography>
            )}
          </Box>
        </Box>
        {isNew ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: SLATE[300] }} />
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: SLATE[500] }}>Not enabled</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <CheckCircleOutlined sx={{ fontSize: "1rem", color: "#22c55e" }} />
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#16a34a" }}>
              Connected: {agentCap?.capability_instance_name || capName}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Tool Schema — editable for all capability types */}
      {capability?.capability_type && (
        <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "16px", p: "24px", mb: 2 }}>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900], mb: 0.5 }}>Tool Schema</Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], mb: 2.5 }}>
            Define the input and output fields for this capability.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>Tool Input Schema (JSON)</Typography>
              <Typography sx={{ ...FIELD_HINT_SX, mt: 0, mb: "8px" }}>Array of fields this tool accepts — e.g. field_name, field_type</Typography>
              <JsonEditor
                value={inputSchema}
                onChange={(val) => {
                  setInputSchema(val);
                  try { if (val) JSON.parse(val); setSchemaErrors((p) => ({ ...p, input: null })); }
                  catch (e) { setSchemaErrors((p) => ({ ...p, input: e.message })); }
                }}
                height="160px"
              />
              {schemaErrors.input && (
                <Typography sx={{ fontSize: "0.75rem", color: "#ef4444", mt: "4px" }}>Invalid JSON: {schemaErrors.input}</Typography>
              )}
            </Box>
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>Tool Output Schema (JSON)</Typography>
              <Typography sx={{ ...FIELD_HINT_SX, mt: 0, mb: "8px" }}>Array of fields this tool returns — e.g. field_name, field_type</Typography>
              <JsonEditor
                value={outputSchema}
                onChange={(val) => {
                  setOutputSchema(val);
                  try { if (val) JSON.parse(val); setSchemaErrors((p) => ({ ...p, output: null })); }
                  catch (e) { setSchemaErrors((p) => ({ ...p, output: e.message })); }
                }}
                height="160px"
              />
              {schemaErrors.output && (
                <Typography sx={{ fontSize: "0.75rem", color: "#ef4444", mt: "4px" }}>Invalid JSON: {schemaErrors.output}</Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Configuration section */}
      <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "16px", p: "24px", mb: 10 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900] }}>
              Configuration for {capName}
            </Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], mt: "2px" }}>
              Customize how this capability behaves for this agent.
            </Typography>
          </Box>
          {!isNew && (
            <Button
              variant="outlined" size="small"
              onClick={() => setConfirmDisable(true)}
              sx={{ borderColor: "#fca5a5", color: "#dc2626", textTransform: "none", fontSize: "0.8125rem", fontWeight: 500, flexShrink: 0, "&:hover": { borderColor: "#ef4444", bgcolor: "#fef2f2" } }}
            >
              Disable Capability
            </Button>
          )}
        </Box>

        {/* Dynamic config fields — works for any capability type automatically */}
        {hasConfigFields && (
          <>
            <DynamicConfigFields
              capConfig={mergedCapConfig}
              values={configValues}
              onChange={handleConfigChange}
              jsonErrors={jsonErrors}
            />
            <Divider sx={{ my: 3, borderColor: SLATE[100] }} />
          </>
        )}

        {/* Python hooks */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mb: 3 }}>
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Before Capability Code (Python)</Typography>
            <Typography sx={{ ...FIELD_HINT_SX, mt: 0, mb: "8px" }}>
              Runs before the capability executes — override to preprocess inputs for this agent
            </Typography>
            <CodeEditor
              value={beforeCode}
              onChange={setBeforeCode}
              language="python"
              placeholder="# e.g. sanitize or transform inputs before execution"
            />
          </Box>
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>After Capability Code (Python)</Typography>
            <Typography sx={{ ...FIELD_HINT_SX, mt: 0, mb: "8px" }}>
              Runs after the capability executes — override to transform the response for this agent
            </Typography>
            <CodeEditor
              value={afterCode}
              onChange={setAfterCode}
              language="python"
              placeholder="# e.g. parse, filter or reshape the response"
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 3, borderColor: SLATE[100] }} />

        {/* Custom Instructions */}
        <Box>
          <Typography component="label" sx={FIELD_LABEL_SX}>Custom Instructions</Typography>
          <Typography sx={{ ...FIELD_HINT_SX, mb: "8px", mt: 0 }}>
            Describe when and how the agent should use this capability
          </Typography>
          <TrimTextField
            fullWidth multiline minRows={4}
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            placeholder={`Use this capability when the user asks about ${capName.toLowerCase()}.`}
            sx={INPUT_SX}
          />
        </Box>
      </Box>

      <BottomActionBar
        name={isNew ? "Enable Capability" : "Save Changes"}
        handleSave={isNew ? handleEnable : handleSave}
        cancelRedirectUrl={`/${app_name}/agent/${id}/capabilities`}
        saving={saving}
        isDirty={isDirty}
      />

      {/* Disable Confirmation Dialog */}
      <Dialog open={confirmDisable} onClose={() => setConfirmDisable(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>Disable Capability?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.875rem", color: SLATE[600] }}>
            This will remove <strong>{capName}</strong> from this agent. You can re-enable it at any time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setConfirmDisable(false)} sx={{ textTransform: "none", borderColor: SLATE[300], color: SLATE[700] }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDisable} disabled={disabling} sx={{ textTransform: "none", bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" }, boxShadow: "none" }}>
            {disabling && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
